import { FC, useEffect, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import classnames from 'classnames'
import { IonGrid } from '@ionic/react'
import { Button, QuizQuestionSection } from '@mytours/components'
import type { QuizAnswerId, QuizQuestionAnswer, QuizQuestionType } from '@mytours/components'
import { QuizAnswerContentContainer as Container, ScreenSection } from 'mytours-read-common'
import { MediaItem, Question } from 'mytours-read-api-typescript'
import db from 'mobile_web/modules/db'
import strings from 'mobile_web/strings'
import AnswerModal from 'mobile_web/modules/quiz/components/AnswerModal'
import { saveResultToDB } from 'mobile_web/modules/quiz/queries'
import { isFreeTextAnswerCorrect } from 'mobile_web/modules/quiz/functions'
import { ImageSizePreset } from 'mobile_web/common_lib/types'
import { BaseUrlEnum, generateFilePath, generateImageUrl } from 'mobile_web/common_lib/utils'
import { getDirMode } from '../../../platform'
import SectionHeader from './SectionHeader'
import '../styles/sectionQuizQuestion.less'

interface SectionQuizQuestionProps {
	section: ScreenSection
	language: string
}

const QuestionTypes = Question.QuestionTypeEnum

const getAudioFilePath = (mediaItem?: MediaItem): string | undefined => {
	if (!mediaItem?.file) {
		return undefined
	}
	const { file, dont_transform: dontTransform } = mediaItem
	const { filename, id, region } = file
	const { base, path } = generateFilePath(String(id), BaseUrlEnum.BunnyUploads, {
		pathType: dontTransform ? 'original' : 'quality_7',
		region,
	})
	return base + path + filename
}

// Manage attempts, saved results, feedback and badges around the shared question component.
const SectionQuizQuestion: FC<SectionQuizQuestionProps & Container.Actions> = ({
	section,
	language,
	toggleBadgeModal,
}) => {
	const [isAnswered, setIsAnswered] = useState(false)
	const [checkedAnswers, setCheckedAnswers] = useState<number[]>([])
	const [textAnswer, setTextAnswer] = useState('')
	const [attemptsUsed, setAttemptsUsed] = useState(0)
	const [answerModalOpen, setAnswerModalOpen] = useState(false)
	const [answeredCorrect, setAnsweredCorrect] = useState(false)
	const [shouldShowBadgeModal, setShowBadgeModal] = useState(false)

	const { quiz_id: quizId, question_id: questionId } = section
	const question = useLiveQuery(
		() => (quizId ? db.quizQuestions.get({ id: questionId, quiz_id: quizId, language }) : undefined),
		[questionId, quizId, language],
	)
	const earnedBadges = useLiveQuery(() => db.recentlyEarnedBadges.toArray())
	const trigger = useLiveQuery(
		() => question && db.triggers.where({ triggerable_id: question.id, trigger_type: 'quiz_question' }).first(),
		[question],
	)

	useEffect(() => {
		if (question && trigger && earnedBadges) {
			const earned = !!earnedBadges.find(badge => badge.badge_id === trigger.badge_id)
			if (!earned) {
				setShowBadgeModal(true)
			}
		}
	}, [question, trigger, earnedBadges])

	const questionType = question?.question_type
	const isImage = questionType === QuestionTypes.SingleImageChoice || questionType === QuestionTypes.MultiImageChoice
	const isMulti = questionType === QuestionTypes.MultiImageChoice || questionType === QuestionTypes.MultiTextChoice
	const isFreeText = questionType === QuestionTypes.FreeText

	// Restore saved answers and attempts when revisiting a question.
	useEffect(() => {
		if (!question) {
			return
		}
		let stale = false
		db.quizResults.get({ question_id: question.id }).then(results => {
			if (!results || stale) {
				return
			}
			if (question.question_type === QuestionTypes.FreeText) {
				setTextAnswer(results.answers as string)
			} else {
				setCheckedAnswers([...(results.answers as number[])])
			}
			setIsAnswered(true)
			setAttemptsUsed(results.attempts_used)
		})
		return () => {
			stale = true
		}
	}, [question])

	const answers = question?.answers ?? []

	const checkCorrect = (given: number[] | string): boolean => {
		if (isFreeText) {
			return isFreeTextAnswerCorrect(answers, given as string)
		}
		const ids = given as number[]
		if (!isMulti) {
			return ids.some(id => !!answers.find(answer => answer.id === id)?.correct)
		}
		const allCorrect = ids.every(id => !!answers.find(answer => answer.id === id)?.correct)
		return allCorrect && answers.filter(answer => answer.correct).length === ids.length
	}

	const handleSubmitAnswer = (given: number[] | string): void => {
		if (!question || !quizId) {
			return
		}
		const submitted = isMulti ? checkedAnswers : given
		setIsAnswered(true)
		setAttemptsUsed(attemptsUsed + 1)
		const isAnsweredCorrect = checkCorrect(submitted)
		setAnsweredCorrect(isAnsweredCorrect)
		if (shouldShowBadgeModal && isAnsweredCorrect && trigger) {
			toggleBadgeModal(true, trigger.badge_id, true, true)
		}
		setAnswerModalOpen(true)
		saveResultToDB(question.id, quizId, submitted, isAnsweredCorrect, attemptsUsed + 1)
	}

	const handleClearCurrentAnswers = (): void => {
		setIsAnswered(false)
		setCheckedAnswers([])
		setTextAnswer('')
	}

	// Single-choice answers submit on selection.
	const handleSelectionChange = (ids: QuizAnswerId[]): void => {
		const next = ids.map(Number)
		setCheckedAnswers(next)
		if (!isMulti) {
			handleSubmitAnswer(next)
		}
	}

	const hasAttempts = attemptsUsed < (question?.max_attempts ?? 0)
	const hasAttemptsAndAnswered = hasAttempts && isAnswered
	const textIsCorrect = isFreeTextAnswerCorrect(answers, textAnswer)

	const renderActions = (): JSX.Element | null => {
		if (isFreeText) {
			return (
				<Button
					variant="clear"
					type="button"
					onClick={hasAttemptsAndAnswered ? handleClearCurrentAnswers : () => handleSubmitAnswer(textAnswer)}
					aria-label={strings.submit}
					disabled={(isAnswered && !hasAttempts) || (textIsCorrect && hasAttempts && isAnswered)}
					className={classnames({
						'-correct': textIsCorrect && isAnswered,
						'-incorrect': !textIsCorrect && isAnswered,
					})}
				>
					{hasAttempts && isAnswered && !textIsCorrect ? (
						strings.try_again
					) : isAnswered && textIsCorrect ? (
						<i className="far fa-circle-check" />
					) : (
						strings.submit
					)}
				</Button>
			)
		}
		if ((isMulti || hasAttemptsAndAnswered) && !answeredCorrect) {
			return (
				<Button
					variant="clear"
					type="button"
					onClick={() => (hasAttemptsAndAnswered ? handleClearCurrentAnswers() : handleSubmitAnswer(checkedAnswers))}
					aria-label={strings.submit}
					disabled={isAnswered && !hasAttempts}
				>
					{hasAttemptsAndAnswered ? strings.try_again : strings.submit}
				</Button>
			)
		}
		return null
	}

	const libraryAnswers: QuizQuestionAnswer[] = answers.map(answer => {
		const file = isImage ? answer.answer_media_item?.file : undefined
		return {
			id: answer.id,
			label: answer.answer_text || '',
			correct: !!answer.correct,
			...(file ? { image: generateImageUrl(file, { imageSizePreset: ImageSizePreset.Row }) } : {}),
		}
	})

	const imageMediaItem = answeredCorrect ? question?.correct_image_media_item : question?.incorrect_image_media_item
	const imageSrc =
		imageMediaItem?.file && generateImageUrl(imageMediaItem.file, { imageSizePreset: ImageSizePreset.Row })
	const audioMediaItem = answeredCorrect ? question?.correct_audio_media_item : question?.incorrect_audio_media_item

	if (!quizId) {
		return <div />
	}

	return (
		<IonGrid
			className={classnames('section-quiz-question', { 'ion-justify-content-end': isFreeText })}
			dir={getDirMode()}
		>
			{question?.title && <SectionHeader title={question.title} />}
			{question && (
				<QuizQuestionSection
					className="block-content"
					dir={getDirMode()}
					dataCy="story-screen-section"
					question={question.question ?? ''}
					questionType={questionType as QuizQuestionType}
					answers={libraryAnswers}
					selectedAnswerIds={checkedAnswers}
					onSelectedAnswerIdsChange={handleSelectionChange}
					textAnswer={textAnswer}
					onTextAnswerChange={setTextAnswer}
					answered={isAnswered}
					canRetry={hasAttempts}
					freeTextLabel={strings.free_text_answer_input_placeholder}
					freeTextPlaceholder={strings.free_text_answer_input_placeholder}
					actions={renderActions()}
				/>
			)}
			<AnswerModal
				isOpen={answerModalOpen}
				onClose={() => setAnswerModalOpen(false)}
				isCorrect={answeredCorrect}
				text={answeredCorrect ? question?.correct_text : question?.incorrect_text}
				imageSrc={imageSrc}
				audioSrc={getAudioFilePath(audioMediaItem)}
			/>
		</IonGrid>
	)
}

const SectionQuizQuestionComponent = Container.default(SectionQuizQuestion)
export default SectionQuizQuestionComponent as FC<SectionQuizQuestionProps>
